// Native-app extras (haptics, share sheet, status bar, back button). Each is a no-op on the
// website; the Capacitor plugins are only imported in the app build.
import { NATIVE } from './platform.ts';

export type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

export function haptic(kind: HapticKind) {
  if (!NATIVE) return;
  void import('@capacitor/haptics')
    .then(({ Haptics, ImpactStyle, NotificationType }) => {
      if (kind === 'success' || kind === 'warning' || kind === 'error') {
        const type = { success: NotificationType.Success, warning: NotificationType.Warning, error: NotificationType.Error }[kind];
        return Haptics.notification({ type });
      }
      return Haptics.impact({ style: { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy }[kind] });
    })
    .catch(() => {});
}

/** The system share sheet in the app; false on the website (use the Web Share API there). */
export async function nativeShare(opts: { title: string; text: string; url: string }): Promise<boolean> {
  if (!NATIVE) return false;
  const { Share } = await import('@capacitor/share');
  await Share.share({ title: opts.title, text: opts.text, url: opts.url, dialogTitle: 'Share' });
  return true;
}

/** Opens a link outside the app (Safari / the browser). */
export async function openExternal(url: string): Promise<boolean> {
  if (!NATIVE) return false;
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url });
  return true;
}

export async function initNative() {
  if (!NATIVE) return;
  document.documentElement.classList.add('native');
  const [{ StatusBar, Style }, { SplashScreen }, { App }] = await Promise.all([
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
    import('@capacitor/app'),
  ]);
  const dark = matchMedia('(prefers-color-scheme: dark)');
  const syncBar = () => void StatusBar.setStyle({ style: dark.matches ? Style.Dark : Style.Light }).catch(() => {});
  syncBar();
  dark.addEventListener('change', syncBar);
  // Android's back button walks back through the app, then leaves it.
  void App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) history.back();
    else void App.exitApp();
  });
  void SplashScreen.hide();
}
